import './index.scss';

import {
  createChart,
  CrosshairMode,
  IChartApi,
  ISeriesApi,
  Logical,
  MouseEventParams,
  PriceScaleMode,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useUpdateEffect } from 'react-use';

import { useThemeColors } from '@/shared/theme';

import { useRelatedChartsEvent } from 'hooks/charts/useRelatedChartsEvent';
import { ButtonFullscreen } from 'shared/components';
import { CHARTS_COLORS } from 'shared/consts/charts';
import { useTypedDispatch } from 'store';
import { setCopyPopupShow } from 'store/slices/ui';
import { bn, Bus } from 'tools';
import { IPriceFormat } from 'types/charts';
import { chartFormatter } from 'utils/charts';
import { formatFiat } from 'utils/formats/formatFiat';

import { BalanceTooltip } from './BalanceTooltip';

import { ChartLoading } from '../controls/ChartLoading';
import { ChartNotFound } from '../controls/ChartNotFound';

type Balance = {
  records: {
    time: UTCTimestamp;
    value: number;
  }[];
  label: string;
};

type BalancesChartProps = {
  loading: boolean;
  chart: string;
  relatedCharts?: string[];
  logicalEvent: string;
  cursorEvent: string;
  timescaleEvent?: string;
  chartMode: PriceScaleMode;
  priceFormat: IPriceFormat;
  balances: Balance[];
  totalBalancesUsd: {
    time: UTCTimestamp;
    value: number;
  }[];
  balancesShow: Record<string, boolean>;
  rightScaleMinWidth?: number;
  onOpenFullscreen?: () => void;
};

export const BalancesChart = memo(
  ({
    loading,
    chart: chartId,
    relatedCharts = [],
    logicalEvent,
    cursorEvent,
    chartMode,
    priceFormat,
    balances,
    totalBalancesUsd,
    balancesShow,
    timescaleEvent,
    rightScaleMinWidth,
    onOpenFullscreen,
  }: BalancesChartProps) => {
    const palette = useThemeColors();
    const dispatch = useTypedDispatch();
    const chartRef = useRef<HTMLDivElement | null>(null);
    const chartAPIRef = useRef<IChartApi | undefined>(undefined);
    const firstLastIndexesRef = useRef<
      { first: number | undefined; last: number | undefined } | undefined
    >(undefined);
    const hoveredIndexRef = useRef<number | undefined>(undefined);

    const balancesSeriesRef = useRef<ISeriesApi<'Line'>[]>([]);

    // Store viewport state for persistence across reinitializations
    const viewportStateRef = useRef<{
      logicalRange?: { from: number; to: number };
      timeRange?: { from: Time; to: Time };
    }>({});

    // LocalStorage key for storing viewport state
    const viewportStateKey = `balanceChart_${chartId}_viewport`;

    // Debounce timeout for saving viewport state
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Save viewport state to localStorage with debounce
    const saveViewportState = useCallback(
      (state: typeof viewportStateRef.current) => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
          try {
            localStorage.setItem(viewportStateKey, JSON.stringify(state));
          } catch (error) {
            console.warn('Failed to save viewport state to localStorage:', error);
          }
        }, 300); // Debounce 300ms
      },
      [viewportStateKey],
    );

    // Load viewport state from localStorage
    const loadViewportState = useCallback((): typeof viewportStateRef.current => {
      try {
        const saved = localStorage.getItem(viewportStateKey);
        return saved ? JSON.parse(saved) : {};
      } catch (error) {
        console.warn('Failed to load viewport state from localStorage:', error);
        return {};
      }
    }, [viewportStateKey]);

    const setFirstLastIndexes = useCallback((p: any) => {
      const first = p ? Math.ceil(p.from) : undefined;
      const last = p ? Math.floor(p.to) : undefined;

      firstLastIndexesRef.current = { first, last };
    }, []);

    const setHoveredIndex = (p: any) => {
      hoveredIndexRef.current = p
        ? p === undefined
          ? firstLastIndexesRef.current?.first
          : p.logical
        : firstLastIndexesRef.current?.last;
    };

    const crosshairMoveHandler = useCallback(
      (param: MouseEventParams<Time>) => {
        if (!param.sourceEvent) return;

        if (param.time) {
          const val = {
            time: param.time,
            logical: param.logical,
          };

          Bus.emit(`${chartId}-${cursorEvent}`, val);
          setHoveredIndex(val);
        } else {
          chartAPIRef.current?.clearCrosshairPosition();
          Bus.emit(`${chartId}-${cursorEvent}`, undefined);
          setHoveredIndex(undefined);
        }
      },
      [chartId, cursorEvent],
    );

    const renderBalanceCharts = useCallback(
      ({
        balancesChartRecords,
        totalBalancesUsd,
        priceFormat,
        balancesShow,
      }: {
        balancesChartRecords: Balance[];
        totalBalancesUsd: {
          time: UTCTimestamp;
          value: number;
        }[];
        priceFormat: IPriceFormat;
        balancesShow: Record<string, boolean>;
      }) => {
        if (!chartAPIRef.current) return;

        if (balancesSeriesRef.current.length !== 0) {
          balancesSeriesRef.current.forEach(serie => {
            if (serie) {
              const serieTitle = serie.options().title;

              const isTotal = serieTitle === 'Total';

              if (isTotal) {
                serie.setData(totalBalancesUsd);
              }

              const findBalances = balancesChartRecords.find(el => el.label === serieTitle);

              if (findBalances) {
                serie.setData(findBalances.records);
              }
            }
          });
        } else if (chartAPIRef.current) {
          const balanceSeries: any[] = [];

          let index = 0;

          for (const balanceItem of balancesChartRecords) {
            const lineSeries = chartAPIRef.current.addLineSeries({
              color: CHARTS_COLORS[index],
              lineWidth: 2,
              baseLineWidth: 1,
              title: balanceItem.label,
              visible: !!balancesShow[balanceItem.label],
            });

            lineSeries.setData(balanceItem.records);
            balanceSeries.push(lineSeries);

            index++;
          }

          const totalLineSeries = chartAPIRef.current.addLineSeries({
            color: CHARTS_COLORS[3],
            lineWidth: 2,
            baseLineWidth: 1,
            title: 'Total',
            visible: priceFormat === 'usd' && !!balancesShow['Total'],
          });

          totalLineSeries.setData(totalBalancesUsd);
          balanceSeries.push(totalLineSeries);

          balancesSeriesRef.current = balanceSeries;
        }

        for (const serie of balancesSeriesRef.current) {
          const serieTitle = serie.options().title;

          serie.applyOptions({
            visible:
              serieTitle === 'Total'
                ? priceFormat === 'usd' && !!balancesShow['Total']
                : !!balancesShow[serieTitle],
          });
        }
      },
      [],
    );

    const initChart = useCallback(() => {
      try {
        try {
          chartAPIRef.current?.remove();
        } catch (error) {}
        balancesSeriesRef.current = [];

        const chart = createChart(chartRef.current as HTMLElement, {
          autoSize: true,
          crosshair: {
            mode: CrosshairMode.Normal,
          },
          rightPriceScale: {
            mode: chartMode,
            borderColor: palette.chart_border_color,
            minimumWidth: rightScaleMinWidth,
          },
          layout: {
            background: {
              color: 'transparent',
            },
            textColor: '#eee',
          },
          grid: {
            horzLines: {
              color: palette.chart_line_color,
            },
            vertLines: {
              color: palette.chart_line_color,
            },
          },
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
            borderColor: palette.chart_border_color,
            rightBarStaysOnScroll: false,
            fixLeftEdge: false,
            fixRightEdge: false,
          },
          localization: {
            priceFormatter: chartFormatter,
            percentageFormatter: (p: number) => `${p.toFixed(2)}%`,
          },
        });
        chartAPIRef.current = chart;

        renderBalanceCharts({
          balancesChartRecords: balances,
          totalBalancesUsd,
          priceFormat,
          balancesShow,
        });

        chart.timeScale().subscribeVisibleLogicalRangeChange(logicalRange => {
          if (!logicalRange) return;

          const val = {
            from: Number(logicalRange.from),
            to: Number(logicalRange.to),
          };

          // Store current viewport state for persistence
          viewportStateRef.current.logicalRange = val;
          saveViewportState(viewportStateRef.current);

          Bus.emit(`${chartId}-${logicalEvent}`, val);
          setFirstLastIndexes(val);
        });

        if (timescaleEvent) {
          chart.timeScale().subscribeVisibleTimeRangeChange(timeRange => {
            if (!timeRange) return;

            // Store current viewport state for persistence
            viewportStateRef.current.timeRange = timeRange;
            saveViewportState(viewportStateRef.current);

            Bus.emit(`${chartId}-${timescaleEvent}`, {
              from: Number(timeRange.from),
              to: Number(timeRange.to),
            });
          });
        }

        chart.subscribeCrosshairMove(crosshairMoveHandler);

        // Load and restore viewport state from localStorage after initialization
        setTimeout(() => {
          const savedState = loadViewportState();
          if (savedState.logicalRange || savedState.timeRange) {
            viewportStateRef.current = savedState;

            if (savedState.logicalRange) {
              chart.timeScale().setVisibleLogicalRange(savedState.logicalRange);
            } else if (savedState.timeRange) {
              chart.timeScale().setVisibleRange(savedState.timeRange);
            }
          }
        }, 0);

        // -----
      } catch (error) {
        console.log('initing balance chart error: ', error);
      }
    }, [
      rightScaleMinWidth,
      chartId,
      logicalEvent,
      chartMode,
      priceFormat,
      balancesShow,
      timescaleEvent,
      crosshairMoveHandler,
      setFirstLastIndexes,
      palette,
      balances,
      renderBalanceCharts,
      totalBalancesUsd,
      saveViewportState,
      loadViewportState,
    ]);

    const timescaleSetter = useCallback((v: unknown) => {
      const _v = v as unknown as { from: Time; to: Time };

      if (chartAPIRef && chartAPIRef.current) {
        chartAPIRef.current.timeScale().setVisibleRange(_v);
      }
    }, []);

    const logicalSetter = useCallback((v: unknown) => {
      const _v = v as unknown as { from: number; to: number };

      if (chartAPIRef && chartAPIRef.current) {
        chartAPIRef.current.timeScale().setVisibleLogicalRange(_v);
      }
    }, []);

    const cursorSetter = useCallback(
      (v: unknown) => {
        if (chartAPIRef.current) {
          const _v = v as unknown as { time: Time; logical: Logical | undefined } | undefined;

          if (!_v) {
            chartAPIRef.current.clearCrosshairPosition();
          } else if (balancesSeriesRef.current.length) {
            if (_v.logical) {
              const position = balances.length ? balances[0].records[_v.logical] : 0;

              chartAPIRef.current.setCrosshairPosition(
                position ? position.value : 0,
                _v.time,
                balancesSeriesRef.current[0],
              );
            } else {
              chartAPIRef.current.setCrosshairPosition(0, _v.time, balancesSeriesRef.current[0]);
            }
          }
        }
      },
      [balances],
    );

    useRelatedChartsEvent({
      events: (relatedCharts ?? []).map(ch => `${ch}-${timescaleEvent}`),
      handler: timescaleSetter,
    });

    useRelatedChartsEvent({
      events: (relatedCharts ?? []).map(ch => `${ch}-${logicalEvent}`),
      handler: logicalSetter,
    });

    useRelatedChartsEvent({
      events: (relatedCharts ?? []).map(ch => `${ch}-${cursorEvent}`),
      handler: cursorSetter,
    });

    useUpdateEffect(() => {
      if (chartAPIRef.current) {
        chartAPIRef.current.applyOptions({ rightPriceScale: { mode: chartMode } });
      }
    }, [chartMode]);

    useUpdateEffect(() => {
      if (chartAPIRef.current) {
        chartAPIRef.current.applyOptions({
          rightPriceScale: {
            borderColor: palette.chart_border_color,
          },
          grid: {
            horzLines: {
              color: palette.chart_line_color,
            },
            vertLines: {
              color: palette.chart_line_color,
            },
          },
          timeScale: {
            borderColor: palette.chart_border_color,
          },
        });
      }
    }, [palette]);

    useEffect(() => {
      const paginationHandler = (data: unknown) => {
        try {
          const { balancesChartRecords, totalBalancesUsd } = data as any;

          renderBalanceCharts({
            balancesChartRecords,
            totalBalancesUsd,
            priceFormat,
            balancesShow,
          });
        } catch (error) {}
      };

      const updateHandler = (data: unknown) => {
        const { balancesChartRecords, totalBalanceUsd } = data as {
          balancesChartRecords: any[];
          totalBalanceUsd: any;
        };

        if (balancesSeriesRef.current.length !== 0) {
          balancesSeriesRef.current.forEach(serie => {
            if (serie) {
              const serieTitle = serie.options().title;

              const isTotal = serieTitle === 'Total';

              if (isTotal) {
                serie.update(totalBalanceUsd);
              }

              const findBalances = balancesChartRecords.find(el => el.label === serieTitle);

              if (findBalances && findBalances.record) {
                serie.update(findBalances.record);
              }
            }
          });
        }
      };

      Bus.on(`${chartId}-update-pagination`, paginationHandler);
      Bus.on(`${chartId}-update-kline`, updateHandler);

      return () => {
        Bus.off(`${chartId}-update-pagination`, paginationHandler);
        Bus.off(`${chartId}-update-kline`, updateHandler);
      };
    }, [chartId, priceFormat, balancesShow, renderBalanceCharts]);

    useEffect(() => {
      if (chartAPIRef.current) {
        renderBalanceCharts({
          balancesChartRecords: balances,
          balancesShow,
          totalBalancesUsd,
          priceFormat,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [balancesShow, priceFormat]);

    useEffect(() => {
      const isBalanceExist = balances[0] && balances[0].records && balances[0].records.length !== 0;

      if (!loading && isBalanceExist) {
        setTimeout(() => {
          initChart();
        }, 0);
      }

      // Cleanup debounce timeout on unmount
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, initChart]);

    const handleCopyBalancesValue = useCallback(() => {
      const hoveredIndex = hoveredIndexRef.current;
      if (!balances || hoveredIndex === undefined) return;

      const totalBalance =
        priceFormat === 'usd' ? (totalBalancesUsd[hoveredIndex]?.value ?? 0) : null;
      const totalBalanceFormatted = totalBalance !== null ? formatFiat(bn(totalBalance), 18) : null;

      const copiedData = balances.map(el => {
        const formattedValue =
          priceFormat === 'usd'
            ? formatFiat(bn(el.records[hoveredIndex]?.value ?? 0), 18)
            : chartFormatter(el.records[hoveredIndex]?.value ?? 0);

        return {
          label: el.label,
          value: formattedValue,
        };
      });

      const totalString = totalBalanceFormatted ? `Total: ${totalBalanceFormatted}\n` : '';
      const balancesString = copiedData.map(item => `${item.label}: ${item.value}`).join('\n');

      const preparedString = totalString + balancesString;

      navigator.clipboard.writeText(preparedString);

      dispatch(setCopyPopupShow(true));
    }, [balances, totalBalancesUsd, dispatch, priceFormat]);

    const onMouseDownHandler = useCallback(() => {
      for (const relatedChart of relatedCharts) {
        Bus.off(`${relatedChart}-${timescaleEvent}`, timescaleSetter);
      }
    }, [relatedCharts, timescaleEvent, timescaleSetter]);

    const onMouseUpHandler = useCallback(() => {
      for (const relatedChart of relatedCharts) {
        Bus.on(`${relatedChart}-${timescaleEvent}`, timescaleSetter);
      }
    }, [relatedCharts, timescaleEvent, timescaleSetter]);

    const isBalanceExist = useMemo(
      () => balances.length !== 0 && balances[0]?.records.length !== 0,
      [balances],
    );

    return (
      <div className="mm-balances-chart-container">
        {onOpenFullscreen && isBalanceExist && !loading && (
          <div className="absolute top-0 right-0 z-[50]">
            <ButtonFullscreen onClick={onOpenFullscreen} />
          </div>
        )}

        {!loading && isBalanceExist && (
          <BalanceTooltip
            chartId={chartId}
            relatedCharts={relatedCharts}
            priceFormat={priceFormat}
            balancesOuter={balances}
            cursorEvent={cursorEvent}
            logicalEvent={logicalEvent}
            totalBalancesUsd={totalBalancesUsd}
          />
        )}

        {!loading ? (
          <div className="chart-wrp">
            <div
              className="chart"
              ref={node => {
                chartRef.current = node;
              }}
              onDoubleClick={handleCopyBalancesValue}
              onMouseDown={onMouseDownHandler}
              onMouseUp={onMouseUpHandler}
            />
          </div>
        ) : (
          <ChartLoading />
        )}

        {!loading && !isBalanceExist && <ChartNotFound text="Chart not found" />}
      </div>
    );
  },
);
